import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BrandShell from "../components/BrandShell";
import { useAuth } from "../auth/AuthContext";
import { ArrowRight, Loader2, Lock, Mail, AlertTriangle } from "lucide-react";

const COOLDOWN_SECONDS = 5;

export default function Login() {
  useEffect(() => { document.title = "Sign In | JC3 Wealth Legacy Group"; }, []);
  const { login } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [state, setState] = useState({ status: "idle", error: "" });
  const [cooldown, setCooldown] = useState(0);
  const tickRef = useRef(null);

  useEffect(() => () => clearInterval(tickRef.current), []);

  const startCooldown = () => {
    setCooldown(COOLDOWN_SECONDS);
    clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) { clearInterval(tickRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (cooldown > 0 || state.status === "loading") return;
    setState({ status: "loading", error: "" });
    try {
      const u = await login(form.email.trim(), form.password);
      nav(u.role === "admin" ? "/dashboard/admin" : "/dashboard/client", { replace: true });
    } catch (err) {
      setState({ status: "error", error: err.message || "Sign-in failed." });
      // Lockout messages (429) don't trigger the local cooldown — server already gates them.
      if (err.status !== 429) startCooldown();
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-jc-gold/30 px-0 py-3 font-body text-base text-jc-white placeholder-jc-muted focus:border-jc-gold focus:outline-none transition-colors";

  const cooldownPct = ((COOLDOWN_SECONDS - cooldown) / COOLDOWN_SECONDS) * 100;

  return (
    <BrandShell dashboardChrome>
      <section data-testid="login-page" className="relative bg-jc-black text-jc-white min-h-[calc(100vh-5rem)] flex items-center overflow-hidden">
        <div className="absolute inset-0 jc-grid-bg" />
        <div className="relative z-10 mx-auto w-full max-w-md px-6 md:px-10 py-16">
          <div className="flex items-center gap-3 justify-center mb-10">
            <span className="inline-block h-8 w-8 border border-jc-gold rotate-45" />
            <span className="font-display text-xl tracking-[0.18em] text-jc-white">JC3 <span className="text-jc-gold">·</span> WEALTH LEGACY</span>
          </div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-jc-gold text-center mb-3">◆ Client Portal</p>
          <h1 data-testid="login-heading" className="font-display text-jc-white text-center" style={{ fontSize: "clamp(2.4rem, 5vw, 3.5rem)", lineHeight: 0.95 }}>
            SIGN <span className="text-jc-gold">IN.</span>
          </h1>

          <form onSubmit={onSubmit} className="mt-10 space-y-6" data-testid="login-form">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.28em] text-jc-gold flex items-center gap-2"><Mail size={12} /> Email</label>
              <input
                data-testid="login-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className={inputCls}
                placeholder="you@example.com"
                autoComplete="email"
                disabled={cooldown > 0}
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.28em] text-jc-gold flex items-center gap-2"><Lock size={12} /> Password</label>
              <input
                data-testid="login-password"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                className={inputCls}
                placeholder="••••••••"
                autoComplete="current-password"
                disabled={cooldown > 0}
              />
            </div>

            <button
              type="submit"
              disabled={state.status === "loading" || cooldown > 0}
              data-testid="login-submit"
              className="jc-btn jc-btn-primary w-full !py-4 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {state.status === "loading" ? (
                <><Loader2 size={16} className="animate-spin" /> SIGNING IN...</>
              ) : cooldown > 0 ? (
                <>WAIT {cooldown}s</>
              ) : (
                <>SIGN IN <ArrowRight size={16} strokeWidth={2.5} /></>
              )}
            </button>

            {/* Cooldown ribbon (after a failed attempt) */}
            {cooldown > 0 && (
              <div data-testid="login-cooldown" className="border border-amber-500/40 bg-amber-500/5 px-4 py-3">
                <div className="flex items-center justify-between gap-3 font-mono text-[11px] uppercase tracking-[0.22em] text-amber-300">
                  <span className="inline-flex items-center gap-2"><AlertTriangle size={12} /> Try again in</span>
                  <span data-testid="cooldown-seconds" className="font-display text-lg text-amber-200 leading-none">{cooldown}s</span>
                </div>
                <div className="mt-2 h-0.5 bg-amber-500/15 overflow-hidden">
                  <div className="h-full bg-amber-400 transition-all duration-1000 ease-linear" style={{ width: `${cooldownPct}%` }} />
                </div>
              </div>
            )}

            {state.status === "error" && cooldown === 0 && (
              <div data-testid="login-error" className="font-mono text-xs uppercase tracking-[0.2em] text-red-400 text-center">{state.error}</div>
            )}
            {state.status === "error" && cooldown > 0 && (
              <div data-testid="login-error" className="font-mono text-xs uppercase tracking-[0.2em] text-red-400 text-center">{state.error}</div>
            )}
          </form>

          <div className="mt-10 space-y-3 text-center">
            <p className="font-body text-base text-jc-muted">
              New client?{" "}
              <Link to="/dashboard/signup" data-testid="signup-link" className="text-jc-gold jc-link-underline">Create your account</Link>
            </p>
            <p>
              <Link to="/dashboard/forgot-password" data-testid="forgot-link" className="font-mono text-xs uppercase tracking-[0.25em] text-jc-muted hover:text-jc-gold-light">
                Forgot your password?
              </Link>
            </p>
          </div>
        </div>
      </section>
    </BrandShell>
  );
}
